import React, { useState } from 'react';
import { Settings, Building } from 'lucide-react';
import { useGetCurrentUserQuery } from '../store/api/apiSlice';
import { useSelector } from 'react-redux';
import { TenantManagement } from '../components/ui/TenantManagement';

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'tenants'>('general');
  
  // Get auth state to determine when API calls should be made
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;
  
  const { data: currentUser, isLoading: loadingUser } = useGetCurrentUserQuery(undefined, {
    // Skip API call until authentication is ready
    skip: !isAuthReady
  });

  // Check if user has superadmin claim
  const hasSuperadminClaim = (user: any) => {
    if (!user?.claims) return false;
    return user.claims.some((claim: any) => claim.claim_type === 'superadmin');
  };

  // Legacy email domain check for transition period
  const isLegacyAppAdmin = (email: string | undefined) => {
    if (!email) return false;
    return email.endsWith('@monkeyjumplabs.com') || email.endsWith('@itasca.ai');
  };

  const userIsSuperadmin = hasSuperadminClaim(currentUser) || isLegacyAppAdmin(currentUser?.email);

  if (loadingUser) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your account and application preferences
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            General
          </button>
          
          {userIsSuperadmin && (
            <button
              onClick={() => setActiveTab('tenants')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tenants'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Building className="w-4 h-4 inline mr-2" />
              Tenant Management
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="card">
          <div className="text-center py-12">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              General Settings
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              General settings and preferences will be available here.
            </p>
            {currentUser && (
              <div className="mt-6 text-left max-w-md mx-auto">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Current User Information
                </h4>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div><strong>Email:</strong> {currentUser.email}</div>
                  <div><strong>Name:</strong> {currentUser.name || 'Not set'}</div>
                  <div><strong>Role:</strong> {currentUser.role}</div>
                  <div><strong>Tenant ID:</strong> {currentUser.tenant_id || 'None (Superadmin)'}</div>
                  
                  {/* Display Claims */}
                  {currentUser.claims && currentUser.claims.length > 0 && (
                    <div>
                      <strong>Claims:</strong>
                      <ul className="mt-1 ml-4 text-xs">
                        {currentUser.claims.map((claim, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {claim.claim_type}
                            </span>
                            {claim.resource_type && (
                              <span className="text-gray-500">
                                ({claim.resource_type})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {userIsSuperadmin && (
                    <div className="text-blue-600 dark:text-blue-400">
                      <strong>Superadmin Access:</strong> Granted
                      {hasSuperadminClaim(currentUser) ? " (Claims-based)" : " (Legacy)"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'tenants' && userIsSuperadmin && (
        <TenantManagement />
      )}
    </div>
  );
}; 